import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Globe, Copy, Check, ExternalLink, ShoppingBag,
  Settings, Palette, MessageSquare, Code2,
  UtensilsCrossed, AlertCircle, RefreshCw,
  Save, Loader2, CalendarPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { TabBar } from '@/components/ui/tab-bar';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth.store';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

// ── Types ─────────────────────────────────────────────────────────────────────

interface WidgetConfig {
  allowedOrderTypes: ('DINE_IN' | 'TAKEAWAY' | 'DELIVERY')[];
  allowedCategoryIds: string[];
  featuredBundleIds: string[];
  minOrderAmount: number;
  deliveryFee: number;
  estimatedPrepMinutes: number;
  acceptingOrders: boolean;
  welcomeMessage: string;
  successMessage: string;
  closedMessage: string;
  primaryColor: string;
  accentColor: string;
}

interface WidgetConfigResponse {
  onlineOrderingEnabled: boolean;
  onlineOrderingSlug: string | null;
  effectiveSlug: string;
  config: WidgetConfig;
  embed: {
    pageUrl: string;
    scriptSnippet: string;
    iframeSnippet: string;
    linkSnippet: string;
  } | null;
}

interface Category { id: string; name: string; }
interface Bundle { id: string; name: string; salePrice: number; }

// ── Zod schema ────────────────────────────────────────────────────────────────

const formSchema = z.object({
  onlineOrderingEnabled: z.boolean(),
  onlineOrderingSlug: z.string().optional().nullable()
    .refine(v => !v || /^[a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?$/.test(v), {
      message: 'Lowercase letters, numbers, and hyphens only',
    }),
  allowedOrderTypes: z.array(z.enum(['DINE_IN', 'TAKEAWAY', 'DELIVERY'])).min(1, 'Select at least one order type'),
  minOrderAmount: z.number().min(0),
  deliveryFee: z.number().min(0),
  estimatedPrepMinutes: z.number().min(1).max(300),
  acceptingOrders: z.boolean(),
  welcomeMessage: z.string().max(300),
  successMessage: z.string().max(300),
  closedMessage: z.string().max(300),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color'),
  accentColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color'),
  allowedCategoryIds: z.array(z.string()),
  featuredBundleIds: z.array(z.string()),
});

type FormValues = z.infer<typeof formSchema>;

// ── Toggle Switch ─────────────────────────────────────────────────────────────

function ToggleSwitch({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${checked ? 'bg-primary' : 'bg-muted'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-card shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`}
      />
    </button>
  );
}

// ── Copy field (inline snippet + copy button, HMS style) ─────────────────────

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  }
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        <code className="flex-1 overflow-x-auto rounded-md border bg-muted px-3 py-2 text-xs break-all">
          {value}
        </code>
        <Button type="button" variant="outline" size="sm" onClick={copy}>
          {copied ? <Check className="h-4 w-4 text-success-text" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

// ── Tab definitions (config only — Embed + Preview are always-visible cards) ──

type TabKey = 'general' | 'menu' | 'branding' | 'messages';

const CONFIG_TABS = [
  { key: 'general' as const, label: 'General', icon: Settings },
  { key: 'menu' as const, label: 'Menu & Bundles', icon: UtensilsCrossed },
  { key: 'branding' as const, label: 'Branding', icon: Palette },
  { key: 'messages' as const, label: 'Messages', icon: MessageSquare },
];

// ── Main Page ────────────────────────────────────────────────────────────────

export function OnlineOrderingWidgetPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission('online-ordering:manage');

  const [activeTab, setActiveTab] = useState<TabKey>('general');
  const [disableOpen, setDisableOpen] = useState(false);

  const { data, isLoading, isError } = useQuery<WidgetConfigResponse>({
    queryKey: ['online-ordering-widget-config'],
    queryFn: () => api.get('/online-ordering-widget/config').then(r => r.data),
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['categories-simple'],
    queryFn: () => api.get('/categories?limit=200').then(r => r.data?.data ?? r.data ?? []),
  });

  const { data: bundleProducts = [] } = useQuery<Bundle[]>({
    queryKey: ['bundle-products-simple'],
    queryFn: () =>
      api.get('/products?limit=200&productType=BUNDLE').then(r => r.data?.data ?? r.data?.products ?? []).catch(() => []),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      onlineOrderingEnabled: false,
      onlineOrderingSlug: '',
      allowedOrderTypes: ['TAKEAWAY'],
      minOrderAmount: 0,
      deliveryFee: 0,
      estimatedPrepMinutes: 20,
      acceptingOrders: true,
      welcomeMessage: 'Order online for fast pickup!',
      successMessage: 'Thank you! Your order has been confirmed.',
      closedMessage: 'We are not accepting online orders right now.',
      primaryColor: '#6366f1',
      accentColor: '#8b5cf6',
      allowedCategoryIds: [],
      featuredBundleIds: [],
    },
  });

  useEffect(() => {
    if (data) {
      const c = data.config;
      form.reset({
        onlineOrderingEnabled: data.onlineOrderingEnabled,
        onlineOrderingSlug: data.onlineOrderingSlug ?? '',
        allowedOrderTypes: (c.allowedOrderTypes ?? ['TAKEAWAY']) as ('DINE_IN' | 'TAKEAWAY' | 'DELIVERY')[],
        minOrderAmount: c.minOrderAmount ?? 0,
        deliveryFee: c.deliveryFee ?? 0,
        estimatedPrepMinutes: c.estimatedPrepMinutes ?? 20,
        acceptingOrders: c.acceptingOrders ?? true,
        welcomeMessage: c.welcomeMessage ?? '',
        successMessage: c.successMessage ?? '',
        closedMessage: c.closedMessage ?? '',
        primaryColor: c.primaryColor ?? '#6366f1',
        accentColor: c.accentColor ?? '#8b5cf6',
        allowedCategoryIds: c.allowedCategoryIds ?? [],
        featuredBundleIds: c.featuredBundleIds ?? [],
      });
    }
  }, [data, form]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      api.put('/online-ordering-widget/config', values).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['online-ordering-widget-config'] });
      toast.success('Online ordering settings saved');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg[0] : (msg ?? 'Failed to save settings'));
    },
  });

  function onSubmit(values: FormValues) {
    mutation.mutate(values);
  }

  const watchEnabled = form.watch('onlineOrderingEnabled');
  const watchTypes = form.watch('allowedOrderTypes');
  const watchPrimary = form.watch('primaryColor');
  const watchAccent = form.watch('accentColor');
  const watchSlug = form.watch('onlineOrderingSlug');
  const effectiveSlug = watchSlug || data?.effectiveSlug || '';
  const baseUrl = window.location.origin;
  // Key includes colors so preview iframe re-renders immediately when branding changes
  const previewKey = `${effectiveSlug}-${watchPrimary}-${watchAccent}`;
  const previewUrl = effectiveSlug ? `${baseUrl}/order/${effectiveSlug}?embed=1` : '';

  // Quick-save just the enabled state (no full form submit) — like HMS
  const toggleEnabled = (val: boolean) => {
    mutation.mutate({ ...form.getValues(), onlineOrderingEnabled: val });
  };

  const tabs = CONFIG_TABS;

  function toggleOrderType(type: 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY') {
    const current = watchTypes ?? [];
    if (current.includes(type)) {
      if (current.length === 1) return;
      form.setValue('allowedOrderTypes', current.filter(t => t !== type));
    } else {
      form.setValue('allowedOrderTypes', [...current, type]);
    }
  }

  function toggleCategoryId(id: string) {
    const current = form.watch('allowedCategoryIds') ?? [];
    if (current.includes(id)) {
      form.setValue('allowedCategoryIds', current.filter(c => c !== id));
    } else {
      form.setValue('allowedCategoryIds', [...current, id]);
    }
  }

  function toggleBundleId(id: string) {
    const current = form.watch('featuredBundleIds') ?? [];
    if (current.includes(id)) {
      form.setValue('featuredBundleIds', current.filter(b => b !== id));
    } else {
      form.setValue('featuredBundleIds', [...current, id]);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-3 text-destructive-text p-6">
        <AlertCircle className="h-5 w-5" />
        <span>Failed to load online ordering settings.</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between flex-wrap gap-4" data-tour="online-ordering-header">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-primary/10">
            <Globe className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Online Ordering Widget</h1>
            <p className="text-sm text-muted-foreground">
              Let customers order from your own website, themed to your brand.
            </p>
          </div>
        </div>
        {canManage && (
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={mutation.isPending}
            className="gap-2"
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save settings
          </Button>
        )}
      </div>

      {/* ── Publish card (always visible, matches HMS "Publish" card) ── */}
      <Card data-tour="online-ordering-publish">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" /> Publish
          </CardTitle>
          <CardDescription>
            Enable the public ordering page and pick its web address.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Enable / disable row */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <p className="font-medium">Accept online orders</p>
              <p className="text-sm text-muted-foreground">
                Customers can browse your menu and order from your website.
              </p>
            </div>
            {canManage && (
              <Controller
                control={form.control}
                name="onlineOrderingEnabled"
                render={({ field }) => (
                  <ToggleSwitch
                    checked={field.value}
                    onChange={(val) => {
                      field.onChange(val);
                      if (!val) setDisableOpen(true);
                    }}
                    disabled={mutation.isPending}
                  />
                )}
              />
            )}
            {!canManage && (
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${watchEnabled ? 'bg-success/10 text-success-text' : 'bg-muted text-muted-foreground'}`}>
                {watchEnabled ? 'Live' : 'Disabled'}
              </span>
            )}
          </div>

          {/* Slug picker */}
          <div className="space-y-1" data-tour="online-ordering-slug">
            <Label>Public link slug</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">/order/</span>
              <Controller
                control={form.control}
                name="onlineOrderingSlug"
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    placeholder={data?.effectiveSlug ?? 'your-restaurant'}
                    disabled={!canManage}
                    className="max-w-xs font-mono"
                  />
                )}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Lowercase letters, numbers and hyphens. Leave blank to use your tenant slug
              {data?.effectiveSlug ? <> (<code>{data.effectiveSlug}</code>)</> : ''}.
            </p>
            {form.formState.errors.onlineOrderingSlug && (
              <p className="text-xs text-destructive-text">{form.formState.errors.onlineOrderingSlug.message}</p>
            )}
          </div>

          {/* Accepting orders quick-toggle */}
          {watchEnabled && (
            <div className="flex items-center justify-between rounded-lg border border-dashed p-3">
              <div>
                <p className="text-sm font-medium">Currently accepting orders</p>
                <p className="text-xs text-muted-foreground">
                  Toggle off temporarily without disabling the widget — shows your "closed" message instead.
                </p>
              </div>
              {canManage && (
                <Controller
                  control={form.control}
                  name="acceptingOrders"
                  render={({ field }) => (
                    <ToggleSwitch checked={field.value} onChange={field.onChange} />
                  )}
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Config tabs (General / Menu / Branding / Messages) ── */}
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <TabBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={key => setActiveTab(key as TabKey)}
        />

        <div className="mt-6">
          {/* ── GENERAL TAB ── */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Order types */}
                <Card data-tour="online-ordering-order-types">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Available Order Types</CardTitle>
                    <CardDescription>Choose which order types customers can place online.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(['TAKEAWAY', 'DELIVERY', 'DINE_IN'] as const).map((type) => {
                        const labels = {
                          TAKEAWAY: { icon: '🛍', label: 'Takeaway', desc: 'Customer picks up' },
                          DELIVERY: { icon: '🚚', label: 'Delivery', desc: 'Deliver to address' },
                          DINE_IN: { icon: '🍽', label: 'Dine In', desc: 'Sit at a table' },
                        };
                        const active = watchTypes?.includes(type);
                        return (
                          <button
                            key={type}
                            type="button"
                            onClick={() => canManage && toggleOrderType(type)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 text-start transition ${active ? 'border-primary bg-primary/10' : 'border-border'} ${!canManage ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}`}
                          >
                            <span className="text-xl">{labels[type].icon}</span>
                            <div className="flex-1">
                              <p className="text-sm font-medium">{labels[type].label}</p>
                              <p className="text-xs text-muted-foreground">{labels[type].desc}</p>
                            </div>
                            <div className={`h-4 w-4 rounded-full border-2 ${active ? 'bg-primary border-primary' : 'border-muted-foreground'}`} />
                          </button>
                        );
                      })}
                    </div>
                    {form.formState.errors.allowedOrderTypes && (
                      <p className="text-xs text-destructive-text mt-2">{form.formState.errors.allowedOrderTypes.message}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Operational settings */}
                <Card data-tour="online-ordering-operational">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Operational Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Min Order Amount</Label>
                      <Controller
                        control={form.control}
                        name="minOrderAmount"
                        render={({ field }) => (
                          <Input
                            type="number"
                            min={0}
                            step={10}
                            {...field}
                            onChange={e => field.onChange(parseFloat(e.target.value) || 0)}
                            disabled={!canManage}
                          />
                        )}
                      />
                      <p className="text-xs text-muted-foreground">0 = no minimum</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Delivery Fee</Label>
                      <Controller
                        control={form.control}
                        name="deliveryFee"
                        render={({ field }) => (
                          <Input
                            type="number"
                            min={0}
                            step={10}
                            {...field}
                            onChange={e => field.onChange(parseFloat(e.target.value) || 0)}
                            disabled={!canManage}
                          />
                        )}
                      />
                      <p className="text-xs text-muted-foreground">0 = free delivery</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Est. Prep Time (minutes)</Label>
                      <Controller
                        control={form.control}
                        name="estimatedPrepMinutes"
                        render={({ field }) => (
                          <Input
                            type="number"
                            min={1}
                            max={300}
                            {...field}
                            onChange={e => field.onChange(parseInt(e.target.value) || 20)}
                            disabled={!canManage}
                          />
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* ── MENU & BUNDLES TAB ── */}
          {activeTab === 'menu' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Card data-tour="online-ordering-categories">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Category Filter</CardTitle>
                    <CardDescription>
                      Only show selected categories. Leave all unchecked to show everything.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {categories.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No categories found.</p>
                    ) : (
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {categories.map((cat) => {
                          const selected = (form.watch('allowedCategoryIds') ?? []).includes(cat.id);
                          return (
                            <Label key={cat.id} className="flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50">
                              <Checkbox
                                checked={selected}
                                onChange={() => canManage && toggleCategoryId(cat.id)}
                                disabled={!canManage}
                              />
                              <span className="text-sm">{cat.name}</span>
                            </Label>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card data-tour="online-ordering-bundles">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Featured Combo Deals</CardTitle>
                    <CardDescription>
                      Pin specific bundle/combo products to the top of the online menu.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {bundleProducts.length === 0 ? (
                      <div className="text-center py-6">
                        <ShoppingBag className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                        <p className="text-sm text-muted-foreground">No combo/bundle products found.</p>
                        <p className="text-xs text-muted-foreground mt-1">Create products with type "Bundle" in the catalog.</p>
                      </div>
                    ) : (
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {bundleProducts.map((b) => {
                          const selected = (form.watch('featuredBundleIds') ?? []).includes(b.id);
                          return (
                            <Label key={b.id} className="flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50">
                              <Checkbox
                                checked={selected}
                                onChange={() => canManage && toggleBundleId(b.id)}
                                disabled={!canManage}
                              />
                              <span className="text-sm flex-1">{b.name}</span>
                              <span className="text-xs text-muted-foreground">PKR {b.salePrice}</span>
                            </Label>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* ── BRANDING TAB ── */}
          {activeTab === 'branding' && (
            <div className="space-y-6">
              <Card data-tour="online-ordering-colors">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5" /> Brand colors
                  </CardTitle>
                  <CardDescription>
                    These colors theme the public ordering page to match your brand.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-6">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Primary color</Label>
                      <div className="flex items-center gap-2">
                        <Controller
                          control={form.control}
                          name="primaryColor"
                          render={({ field }) => (
                            <input
                              type="color"
                              value={field.value || '#6366f1'}
                              onChange={e => field.onChange(e.target.value)}
                              disabled={!canManage}
                              className="h-9 w-12 cursor-pointer rounded border"
                              aria-label="Primary color"
                            />
                          )}
                        />
                        <Controller
                          control={form.control}
                          name="primaryColor"
                          render={({ field }) => (
                            <Input
                              {...field}
                              className="w-32 font-mono text-xs"
                              placeholder="#6366f1"
                              disabled={!canManage}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Accent color</Label>
                      <div className="flex items-center gap-2">
                        <Controller
                          control={form.control}
                          name="accentColor"
                          render={({ field }) => (
                            <input
                              type="color"
                              value={field.value || '#8b5cf6'}
                              onChange={e => field.onChange(e.target.value)}
                              disabled={!canManage}
                              className="h-9 w-12 cursor-pointer rounded border"
                              aria-label="Accent color"
                            />
                          )}
                        />
                        <Controller
                          control={form.control}
                          name="accentColor"
                          render={({ field }) => (
                            <Input
                              {...field}
                              className="w-32 font-mono text-xs"
                              placeholder="#8b5cf6"
                              disabled={!canManage}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Inline color swatch preview */}
                  <div className="rounded-xl overflow-hidden border max-w-xs">
                    <div className="p-4 text-primary-foreground" style={{ background: watchPrimary ?? '#6366f1' }}>
                      <p className="font-bold text-sm">🍔 Your Restaurant</p>
                      <p className="text-xs opacity-80 mt-0.5">Order online for fast pickup!</p>
                    </div>
                    <div className="p-3 bg-card space-y-2">
                      <div className="h-6 rounded" style={{ background: watchPrimary ?? '#6366f1', opacity: 0.15 }} />
                      <div className="h-6 w-2/3 rounded bg-muted" />
                      <button
                        type="button"
                        className="w-full py-1.5 rounded text-primary-foreground text-xs font-medium"
                        style={{ background: watchPrimary ?? '#6366f1' }}
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── MESSAGES TAB ── */}
          {activeTab === 'messages' && (
            <div className="space-y-4">
              <Card data-tour="online-ordering-messages">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" /> Customer messages
                  </CardTitle>
                  <CardDescription>Customize what customers see at key points in the ordering flow.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(
                    [
                      { name: 'welcomeMessage' as const, label: 'Welcome message', hint: 'Shown at the top of the ordering page' },
                      { name: 'successMessage' as const, label: 'Order confirmed message', hint: 'Shown after a successful order placement' },
                      { name: 'closedMessage' as const, label: 'Closed / paused message', hint: "Shown when 'Accepting orders' is turned off" },
                    ]
                  ).map(({ name, label, hint }) => (
                    <div key={name} className="space-y-1">
                      <Label>{label}</Label>
                      <Controller
                        control={form.control}
                        name={name}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder={hint}
                            disabled={!canManage}
                            maxLength={300}
                          />
                        )}
                      />
                      <p className="text-xs text-muted-foreground">{hint}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </form>

      {/* ── Embed codes card (always visible when enabled, matches HMS "Add it to your website") ── */}
      {data?.embed && (
        <Card data-tour="online-ordering-embed">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5" /> Add it to your website
            </CardTitle>
            <CardDescription>
              Copy one snippet into your site's HTML. The widget resizes itself automatically.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyField
              label="1-line script tag (recommended) — paste into your site's <body>"
              value={data.embed.scriptSnippet}
            />
            <CopyField
              label="Inline iframe — paste anywhere in your HTML"
              value={data.embed.iframeSnippet}
            />
            <CopyField
              label="Plain link — opens ordering page in a new tab"
              value={data.embed.linkSnippet}
            />
            <a
              href={data.embed.pageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-text hover:underline"
            >
              Open public ordering page <ExternalLink className="h-3 w-3" />
            </a>

            {/* Instructions panel */}
            <div className="rounded-lg border bg-muted/40 p-4 space-y-3 text-sm">
              <p className="font-medium flex items-center gap-2">
                <Code2 className="h-4 w-4 text-muted-foreground" />
                How to embed on your website
              </p>
              <ol className="space-y-2 text-muted-foreground list-none">
                <li className="flex gap-2">
                  <span className="shrink-0 h-5 w-5 rounded-full bg-primary/10 text-primary-text text-xs flex items-center justify-center font-semibold">1</span>
                  <span>Copy the <strong className="text-foreground">1-line script tag</strong> above.</span>
                </li>
                <li className="flex gap-2">
                  <span className="shrink-0 h-5 w-5 rounded-full bg-primary/10 text-primary-text text-xs flex items-center justify-center font-semibold">2</span>
                  <span>Paste it inside the <code className="bg-muted px-1 py-0.5 rounded text-xs">&lt;body&gt;</code> of the page on your website where you want the ordering widget to appear.</span>
                </li>
                <li className="flex gap-2">
                  <span className="shrink-0 h-5 w-5 rounded-full bg-primary/10 text-primary-text text-xs flex items-center justify-center font-semibold">3</span>
                  <span>The widget creates an iframe automatically and <strong className="text-foreground">resizes itself</strong> as customers browse — no fixed height needed.</span>
                </li>
                <li className="flex gap-2">
                  <span className="shrink-0 h-5 w-5 rounded-full bg-primary/10 text-primary-text text-xs flex items-center justify-center font-semibold">4</span>
                  <span>Optional: add <code className="bg-muted px-1 py-0.5 rounded text-xs">data-target="#my-div"</code> to mount inside a specific element, or <code className="bg-muted px-1 py-0.5 rounded text-xs">data-height="900"</code> to set a minimum height.</span>
                </li>
              </ol>
              <p className="text-xs text-muted-foreground border-t pt-3">
                Works on any website — WordPress, Wix, Squarespace, Webflow, plain HTML. No login required for customers.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Disabled state — show placeholder embed card */}
      {!data?.embed && !isLoading && (
        <Card className="border-dashed" data-tour="online-ordering-embed">
          <CardContent className="flex flex-col items-center justify-center py-10 text-center">
            <Code2 className="h-10 w-10 text-muted-foreground mb-3 opacity-40" />
            <p className="font-medium text-muted-foreground">Embed codes will appear here</p>
            <p className="text-sm text-muted-foreground mt-1">
              Enable online ordering in the Publish section above and save to get your embed snippets.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── Live preview (always visible when enabled, matches HMS "Live preview") ── */}
      {data?.embed && watchEnabled && previewUrl && (
        <Card data-tour="online-ordering-preview">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Live preview</CardTitle>
                <CardDescription>Exactly what your customers see. Refreshes when you change colors.</CardDescription>
              </div>
              <a href={previewUrl.replace('?embed=1', '')} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Full page
                </Button>
              </a>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-hidden rounded-b-lg">
            <iframe
              key={previewKey}
              src={previewUrl}
              className="w-full border-0 rounded-b-lg"
              style={{ height: 640 }}
              title="Online ordering widget preview"
            />
          </CardContent>
        </Card>
      )}

      {/* ── Disable confirmation dialog (matches HMS pattern) ── */}
      <ConfirmDialog
        open={disableOpen}
        title="Disable online ordering?"
        description="Customers will no longer be able to order from your website. Existing orders are unaffected."
        confirmLabel="Disable"
        onConfirm={() => {
          setDisableOpen(false);
          form.setValue('onlineOrderingEnabled', false);
          toggleEnabled(false);
        }}
        onCancel={() => {
          setDisableOpen(false);
          form.setValue('onlineOrderingEnabled', true);
        }}
      />
    </div>
  );
}
